import cn from 'classnames'
import {FC, useEffect, useRef, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {CheckCircleIcon} from '@/components/common/icons/CheckCircleIcon'
import {useMobile} from '@/components/context/MobileProvider'
import {useTrademarkVerificationContent} from '@/content/trademarkVerification'
import {useTranslation} from '@/i18n'
import {FORM_SUMMARY_HEIGHT_MOBILE, HEADER_HEIGHT_DESKTOP} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {getScrollBehavior} from '@/utils/ui/scrollBehavior'

import {FormStepHeaderActionContext} from './FormStepHeaderAction'

type FormStepProps = {
  stepNumber: number
  title: string
  isCurrent: boolean
  isCompleted: boolean
  isTouched: boolean
  onClick?: () => void
}

export const FormStep: FC<React.PropsWithChildren<FormStepProps>> = ({
  stepNumber,
  title,
  isCurrent,
  isCompleted,
  isTouched,
  onClick,
  children,
}) => {
  const {t} = useTranslation()
  const {editButton} = useTrademarkVerificationContent()

  const isMobile = useMobile()
  const stepRef = useRef<HTMLDivElement>(null)
  const [headerActionSlot, setHeaderActionSlot] = useState<HTMLDivElement | null>(null)
  const headerHeight = isMobile ? FORM_SUMMARY_HEIGHT_MOBILE : HEADER_HEIGHT_DESKTOP
  const topOffset = parseInt(headerHeight, 10)

  useEffect(() => {
    if (isCurrent && stepRef.current) {
      const rect = stepRef.current.getBoundingClientRect()
      const stepOffset = topOffset + (rect.height < 80 ? 24 : 0)
      const isVisible = rect.top >= stepOffset && rect.bottom <= window.innerHeight

      if (!isVisible) {
        window.scrollTo({
          top: window.scrollY + rect.top - stepOffset,
          behavior: getScrollBehavior(),
        })
      }
    }
  }, [isCurrent, topOffset])

  const isClickable = (isCompleted && !isCurrent) || (isTouched && !isCurrent)
  const isDisabled = !isCompleted && !isTouched

  return (
    <>
      <section className={cn('step', isCurrent && 'current')}>
        <div
          className={cn('header', isClickable && 'clickable')}
          role="button"
          tabIndex={0}
          onClick={isClickable ? onClick : undefined}
          ref={stepRef}
        >
          <div className="wrapper">
            {isCompleted ? (
              <div className="icon">
                <CheckCircleIcon backgroundColor={theme.palette.success.main} />
              </div>
            ) : (
              <div className={cn('icon number', isDisabled && 'disabled')}>
                <Text
                  variant={TextVariant.caption}
                  fontWeight={TextFontWeight.bold}
                  color={isDisabled ? theme.palette.text.disabled : theme.palette.text.default}
                >
                  {stepNumber}
                </Text>
              </div>
            )}
            <Text
              variant={TextVariant.h3}
              element="h2"
              color={isDisabled ? theme.palette.text.disabled : theme.palette.text.default}
            >
              {title}
            </Text>
          </div>
          {isClickable && (
            <Button
              variant={ButtonVariant.tertiary}
              size={ButtonSize.small}
              type="button"
              data-test="editButton"
              fontWeight={TextFontWeight.medium}
              textVariant={TextVariant.subtitle13}
            >
              {t(editButton)}
            </Button>
          )}
          {isCurrent && <div className="header-action" ref={setHeaderActionSlot} />}
        </div>
        {isCurrent && (
          <FormStepHeaderActionContext.Provider value={headerActionSlot}>
            {children}
          </FormStepHeaderActionContext.Provider>
        )}
      </section>

      <style jsx>{`
        .step:not(.current:last-child) {
          border-bottom: 1px solid ${theme.palette.gray[200]};
        }
        .current {
          padding-bottom: ${theme.spacing[5]};
        }
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: ${theme.spacing[4]} 0;
        }
        .step:first-of-type .header {
          padding-top: 0;
        }
        .current .header {
          margin-bottom: ${theme.spacing[4]};
          flex-wrap: wrap;
          row-gap: ${theme.spacing[2.5]};
        }
        .header-action {
          margin-left: auto;
        }
        .header-action:empty {
          display: none;
        }
        .wrapper {
          display: flex;
          align-items: center;
          margin-right: ${theme.spacing[3]};
        }
        .icon {
          margin-right: 12px;
        }
        .number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          flex: 0 0 20px;
          border-radius: 50%;
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.text.default};
        }
        .disabled {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[300]};
        }
        .clickable {
          cursor: pointer;
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .step:first-of-type .header {
            padding-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
